import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { caseStudies } from "../constant/caseStudies";
import { ClientLogo } from "./ClientLogo";
import "./CaseStudies.css";

const CaseStudies = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Case Studies | Tedekstra Digital Transformation & Software Solutions";
    let metaTag = document.querySelector("meta[name='description']");
    const description =
      "Explore real-world case studies showcasing how Tedekstra empowers healthcare, non-profit, and enterprise clients with bespoke software and operational transformation.";
    if (metaTag) {
      metaTag.setAttribute("content", description);
    } else {
      metaTag = document.createElement("meta");
      metaTag.name = "description";
      metaTag.content = description;
      document.head.appendChild(metaTag);
    }

    if (window.AOS) {
      window.AOS.refresh();
    }
  }, []);

  const categories = ["All", ...new Set(caseStudies.map((cs) => cs.category))];

  const filteredCaseStudies =
    selectedCategory === "All"
      ? caseStudies
      : caseStudies.filter((cs) => cs.category === selectedCategory);

  return (
    <>
      {/* Hero Section */}
      <section className="case-hub-hero text-center">
        <div className="container">
          <span className="case-hub-badge">
            <i className="fa-solid fa-award me-1"></i> Verified Case Studies
          </span>
          <h1 className="case-hub-main-title">
            Transforming Operations Through <br />
            <span style={{ color: "#769e2c" }}>Bespoke Digital Innovation</span>
          </h1>
          <p className="case-hub-main-subtitle">
            Explore authentic case studies detailing how Tedekstra eliminates operational bottlenecks,
            automates manual workflows, and scales growth with zero upfront development costs.
          </p>

          {/* Filter Pills */}
          <div className="case-filter-wrapper">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`case-filter-pill ${selectedCategory === cat ? "active" : ""}`}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Showcase List */}
      <section className="pb-5 bg-white">
        <div className="container" style={{ maxWidth: "1020px" }}>
          <div className="row">
            {filteredCaseStudies.map((study) => (
              <div className="col-12" key={study.id}>
                <div className="case-showcase-card">
                  {/* Top Green Banner */}
                  <div className="case-showcase-header">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <span className="case-showcase-tag">CASE STUDY</span>
                      <span
                        className="badge badge-light px-3 py-1 text-dark font-weight-bold"
                        style={{ borderRadius: "50px", fontSize: "0.8rem" }}
                      >
                        {study.category}
                      </span>
                    </div>
                    <h2 className="case-showcase-title">{study.client}</h2>
                    <div className="case-showcase-subtitle">{study.subtitle}</div>
                  </div>

                  {/* Body */}
                  <div className="case-showcase-body">
                    <div className="row align-items-center">
                      <div className="col-lg-8">
                        <p className="text-muted mb-4" style={{ fontSize: "1rem", lineHeight: "1.7" }}>
                          {study.clientOverview[0]}
                        </p>

                        {/* Metrics Table */}
                        <div className="table-responsive mb-4">
                          <table className="pdf-metrics-styled-table">
                            <thead>
                              <tr>
                                <th>Key Metric</th>
                                <th>Result</th>
                              </tr>
                            </thead>
                            <tbody>
                              {study.keyMetricsTable.slice(0, 3).map((row, rIdx) => (
                                <tr key={rIdx}>
                                  <td>{row.metric}</td>
                                  <td>{row.result}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Action Buttons */}
                        <div className="d-flex align-items-center flex-wrap">
                          <Link
                            to={`/case-studies/${study.slug}`}
                            className="btn btn-dark rounded-pill px-4 py-2 font-weight-bold"
                            style={{ background: "#121417", borderColor: "#121417", marginRight: "14px", marginBottom: "8px" }}
                          >
                            Read Full Case Study <i className="fa-solid fa-arrow-right ml-1"></i>
                          </Link>
                          <a
                            href={study.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-danger rounded-pill px-4 py-2 font-weight-bold"
                            style={{ marginBottom: "8px" }}
                          >
                            <i className="fa-regular fa-file-pdf mr-1"></i> Download PDF
                          </a>
                        </div>
                      </div>

                      {/* Right Logo & Tags */}
                      <div className="col-lg-4 d-flex flex-column align-items-center justify-content-center mt-4 mt-lg-0">
                        <ClientLogo type={study.logoType} />
                        <div className="text-muted small text-center mt-3 font-weight-bold">
                          {study.industry}
                        </div>
                        <div className="text-muted small text-center mt-1">
                          Delivery Duration: <strong>{study.duration}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 bg-light">
        <div className="container" style={{ maxWidth: "1020px" }}>
          <div
            className="p-5 text-center text-white"
            style={{
              borderRadius: "12px",
              background: "linear-gradient(135deg, #121417 0%, #1e293b 100%)",
              boxShadow: "0 16px 36px rgba(0,0,0,0.12)"
            }}
          >
            <h2 className="font-weight-bold mb-3" style={{ fontSize: "2.1rem" }}>
              Bespoke Solutions. Enterprise Capability. No Upfront Development Cost.
            </h2>
            <p className="mx-auto text-light mb-4" style={{ maxWidth: "680px", fontSize: "1.05rem" }}>
              Tedekstra helps ambitious organisations transform operations through bespoke software solutions,
              digital transformation, ERP implementations, and managed technology services.
            </p>
            <div className="d-flex justify-content-center flex-wrap gap-3">
              <Link
                to="/contact"
                className="btn btn-warning px-4 py-3 font-weight-bold"
                style={{
                  background: "#ff5b00",
                  borderColor: "#ff5b00",
                  color: "#ffffff",
                  borderRadius: "999px"
                }}
              >
                Schedule a Consultation <i className="fa-solid fa-arrow-right ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CaseStudies;
